import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useActOnPriceResearch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { errorMessage } from "./cost-recipe-panel";
import { asDoc, gbp, type ApplyPreview, type BatchDetail, type ResearchType } from "@/lib/price-review";

export function PriceReviewUndo({ detail, selectedIds, disabled, onUndone, researchType }: {
  detail: BatchDetail; selectedIds: string[]; disabled: boolean; onUndone: (document: unknown) => void; researchType: ResearchType;
}) {
  const labour = researchType === "labour";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<ApplyPreview | null>(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const previewM = useActOnPriceResearch();
  const undoM = useActOnPriceResearch();
  function start() {
    setOpen(true); setPreview(null); setError(null); setRevision(detail.revision);
    previewM.mutate({ data: { action: "preview-undo", batchId: detail.id, revision: detail.revision, selectedIds } }, {
      onSuccess: d => setPreview(asDoc<ApplyPreview>(d)),
      onError: e => setError(errorMessage(e)),
    });
  }
  function restore() {
    if (!preview) return;
    setError(null);
    undoM.mutate({ data: { action: "undo", batchId: detail.id, revision, selectedIds: preview.selectedIds, token: preview.token, confirm: true } }, {
      onSuccess: d => {
        onUndone(d); setOpen(false); setPreview(null);
        void qc.invalidateQueries({ predicate: q => q.queryKey.some(k => typeof k === "string" && k.includes("cost-library")) });
      },
      onError: e => { setError(errorMessage(e)); setPreview(null); },
    });
  }
  return <>
    <Button size="sm" variant="outline" data-testid="button-preview-undo"
      disabled={disabled || !selectedIds.length || previewM.isPending || undoM.isPending} onClick={start}>
      Undo
    </Button>
    <Dialog open={open} onOpenChange={o => { if (!undoM.isPending) setOpen(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="dialog-undo-prices">
        <DialogHeader>
          <DialogTitle>Undo applied {labour ? "labour" : "material"} updates</DialogTitle>
          <DialogDescription>Restore the recorded previous {labour ? "labour allowances" : "material prices, quantities and waste allowances"} and work-item rates in the development Cost Library only. Saved quotes, personal rates and live prices stay unchanged.</DialogDescription>
        </DialogHeader>
        {previewM.isPending && <p role="status">Checking previous rates and later edits…</p>}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {preview && <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left"><th className="p-2">Work item</th><th className="p-2">{labour ? "Labour" : "Materials"} now</th><th className="p-2">Restore {labour ? "labour" : "materials"}</th><th className="p-2">Typical rate now → restored</th></tr></thead>
              <tbody>{preview.impacts.map(p => <tr key={p.taskId} className="border-b">
                <td className="p-2">{p.name}</td><td className="p-2">{gbp(labour ? p.before.labour : p.before.materials)}</td>
                <td className="p-2">{gbp(labour ? p.after.labour : p.after.materials)}</td><td className="p-2">{gbp(p.before.typical)} → {gbp(p.after.typical)}</td>
              </tr>)}</tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground">Selected proposals will return to Pending for review. The original update and its reversal remain in audit history. Any later changes block undo rather than being overwritten.</p>
        </>}
        <DialogFooter>
          <Button variant="ghost" disabled={undoM.isPending} onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={!preview || previewM.isPending || undoM.isPending} onClick={restore} data-testid="button-confirm-undo">
            {undoM.isPending ? "Restoring…" : "Restore previous rates"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
